import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';

import {
  DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS,
  type BrandSignatureFrameSettings,
  type CreateBrandPayload,
} from '../../models/brand.models';
import { BrandApiService } from '../../services/brand-api.service';
import { GlobalFeedbackService } from '../../services/global-feedback.service';

type CreateBrandTabId = 'general' | 'advanced' | 'email-content';
type PreviewModeId = 'email' | 'signer';
type DeviceModeId = 'desktop' | 'mobile';

interface CreateBrandDraft {
  name: string;
  isDefault: boolean;
  logoDataUrl?: string;
  logoFileName: string;
  emailDisplayName: string;
  emailBackgroundColor: string;
  primaryButtonColor: string;
  primaryButtonTextColor: string;
  showPlatformBranding: boolean;
  signatureFrame: BrandSignatureFrameSettings;
}

@Component({
  selector: 'app-create-brand',
  imports: [CommonModule, FormsModule],
  templateUrl: './create-brand.component.html',
  styleUrl: './create-brand.component.scss',
})
export class CreateBrandComponent {
  readonly disabledTabs: CreateBrandTabId[] = ['email-content'];

  activeTab: CreateBrandTabId = 'general';
  previewMode: PreviewModeId = 'email';
  deviceMode: DeviceModeId = 'desktop';
  saving = false;
  dragActive = false;
  logoErrorMessage = '';
  nameErrorMessage = '';

  draft: CreateBrandDraft = {
    name: '',
    isDefault: false,
    logoDataUrl: undefined,
    logoFileName: '',
    emailDisplayName: '',
    emailBackgroundColor: '#f8fafc',
    primaryButtonColor: '#7E21D9',
    primaryButtonTextColor: '#ffffff',
    showPlatformBranding: true,
    signatureFrame: {
      enabled: true,
      showRecipientName: true,
      showRecipientEmail: true,
      showTimestamp: true,
    },
  };

  @ViewChild('logoInput') private logoInput?: ElementRef<HTMLInputElement>;

  private readonly brandsApi = inject(BrandApiService);
  private readonly feedback = inject(GlobalFeedbackService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  get previewBrandInitials(): string {
    const name = this.draft.name.trim();
    if (!name) {
      return 'NB';
    }

    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  get hasLogoPreview(): boolean {
    return typeof this.draft.logoDataUrl === 'string' && this.draft.logoDataUrl.length > 0;
  }

  get previewSignatureFrame(): BrandSignatureFrameSettings {
    return this.draft.signatureFrame;
  }

  goBack(): void {
    this.feedback.clearError();
    void this.router.navigate(['/settings/branding']);
  }

  selectTab(tab: CreateBrandTabId): void {
    if (this.disabledTabs.includes(tab)) {
      return;
    }

    this.activeTab = tab;
  }

  selectPreviewMode(mode: PreviewModeId): void {
    this.previewMode = mode;
  }

  selectDeviceMode(mode: DeviceModeId): void {
    this.deviceMode = mode;
  }

  triggerLogoBrowse(): void {
    this.logoInput?.nativeElement.click();
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    void this.readLogoFile(file);
  }

  onLogoDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = true;
  }

  onLogoDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = false;
  }

  onLogoDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file) {
      return;
    }

    void this.readLogoFile(file);
  }

  applyLogoDataUrl(dataUrl: string, fileName = 'brand-logo.png'): void {
    this.draft = {
      ...this.draft,
      logoDataUrl: dataUrl,
      logoFileName: fileName,
    };
    this.logoErrorMessage = '';
    this.cdr.markForCheck();
  }

  clearLogo(): void {
    this.draft = {
      ...this.draft,
      logoDataUrl: undefined,
      logoFileName: '',
    };
    this.logoErrorMessage = '';

    if (this.logoInput?.nativeElement) {
      this.logoInput.nativeElement.value = '';
    }
  }

  saveBrand(): void {
    this.nameErrorMessage = '';
    this.feedback.clearError();

    const name = this.draft.name.trim();
    if (!name) {
      this.nameErrorMessage = 'Brand name is required.';
      return;
    }

    const payload: CreateBrandPayload = {
      name,
      isDefault: this.draft.isDefault,
      logoDataUrl: this.draft.logoDataUrl,
      emailDisplayName: this.draft.emailDisplayName.trim() || undefined,
      emailBackgroundColor: this.draft.emailBackgroundColor,
      primaryButtonColor: this.draft.primaryButtonColor,
      primaryButtonTextColor: this.draft.primaryButtonTextColor,
      showPlatformBranding: this.draft.showPlatformBranding,
      signatureFrame: { ...this.draft.signatureFrame },
    };

    this.saving = true;
    this.brandsApi
      .createBrand(payload)
      .pipe(
        finalize(() => {
          this.saving = false;
        }),
      )
      .subscribe({
        next: () => {
          this.feedback.clearError();
          void this.router.navigate(['/settings/branding']);
        },
        error: (error) => {
          this.feedback.showNormalizedError(error, {
            fallbackMessage: 'Unable to create brand. Please try again.',
          });
        },
      });
  }

  private async readLogoFile(file: File): Promise<void> {
    const acceptedTypes = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml']);
    if (!acceptedTypes.has(file.type)) {
      this.logoErrorMessage = 'Only PNG, JPG, JPEG, and SVG files are supported.';
      return;
    }

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.onerror = () => reject(new Error('Failed to read logo file'));
      reader.readAsDataURL(file);
    }).catch(() => '');

    if (!dataUrl) {
      this.logoErrorMessage = 'Unable to load this logo file. Please try another one.';
      return;
    }

    this.applyLogoDataUrl(dataUrl, file.name);
  }
}
